import React, { useState } from "react";

/**
 * Il modulo contatti dentro la chat.
 *
 * Differenza importante rispetto alle card prodotto: alla conferma NON manda
 * un messaggio in chat. I valori vanno diritti al server su una rotta
 * dedicata, cosi' il modello non vede mai nome, email e telefono e non puo'
 * riscriverli.
 *
 * La validazione qui e' solo per dare una risposta immediata: quella che
 * conta la rifa il server, che non si fida di cio' che arriva dal browser.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Stessi limiti e tipi del server (apps/api/src/services/contactFiles.js): qui
// servono solo a rispondere subito, la verifica vera la rifa il server sui byte.
const FILE_MAX_BYTES = 10 * 1024 * 1024;
const FILE_ACCEPT = {
  both: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
  images: ["image/jpeg", "image/png", "image/webp"],
  documents: ["application/pdf"],
};
const FILE_HINT = {
  both: "Scegli un'immagine o un PDF",
  images: "Scegli un'immagine (JPG, PNG, WEBP)",
  documents: "Scegli un PDF",
};

function formatSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

function validateLocally(fields, values, files = {}) {
  const errors = {};

  for (const field of fields) {
    const value = values[field.id];

    if (field.type === "file") {
      if (files[field.id]?.status === "uploading") {
        errors[field.id] = "Attendi la fine del caricamento";
      } else if (field.required && !value) {
        errors[field.id] = "Carica un file";
      }
      continue;
    }

    if (field.type === "consent") {
      if (field.required && value !== true) {
        errors[field.id] = "Devi accettare per continuare";
      }
      continue;
    }

    if (field.type === "multi_choice") {
      const chosen = Array.isArray(value) ? value : [];
      if (field.required && chosen.length === 0) {
        errors[field.id] = field.min > 1 ? `Scegli almeno ${field.min} opzioni` : "Scegli almeno un'opzione";
      } else if (field.min && chosen.length && chosen.length < field.min) {
        errors[field.id] = `Scegli almeno ${field.min} opzioni`;
      } else if (field.max && chosen.length > field.max) {
        errors[field.id] = `Puoi sceglierne al massimo ${field.max}`;
      }
      continue;
    }

    const text = (value ?? "").toString().trim();
    if (!text) {
      if (field.required) errors[field.id] = "Campo obbligatorio";
      continue;
    }
    if (field.type === "text" && field.mode === "email" && !EMAIL_RE.test(text)) {
      errors[field.id] = "Indirizzo email non valido";
    }
    if (field.type === "text" && field.mode === "phone") {
      // Come il server (contactForm.js, normalizePhone): cifre, spazi, punti,
      // trattini, parentesi e barre, con un "+" solo all'inizio. "333 1234567
      // dopo le 18" non e' un numero.
      const cleaned = text.replace(/[\s.\-()\/]/g, "");
      if (!/^\+?\d{6,15}$/.test(cleaned)) {
        errors[field.id] = "Numero di telefono non valido";
      }
    }
  }

  return errors;
}

/**
 * @param {boolean} [summaryNote] - modulo senza riepilogo in vista dopo una
 *   chat (aperto dal pulsante, o il bot non l'ha scritto): il riepilogo lo fa
 *   il server all'invio, e il visitatore va avvisato.
 */
export default function ContactFormCard({ card, onSubmit, onUpload, summaryNote = false }) {
  const fields = Array.isArray(card.fields) ? card.fields : [];

  const [values, setValues] = useState(() => {
    const initial = {};
    for (const field of fields) {
      if (field.type === "multi_choice") initial[field.id] = [];
      else if (field.type === "consent") initial[field.id] = false;
      else initial[field.id] = "";
    }
    return initial;
  });
  const [errors, setErrors] = useState({});
  const [serverErrors, setServerErrors] = useState([]);
  const [sending, setSending] = useState(false);
  // Un modulo gia' inviato resta un ringraziamento anche dopo aver ricaricato
  // la pagina: ripresentarlo vuoto farebbe pensare che l'invio non sia partito.
  const [sentMessage, setSentMessage] = useState(
    card.sent
      ? card.sent_message || card.thank_you || "Grazie, abbiamo ricevuto la tua richiesta."
      : null
  );
  // Campo esca: sta fuori schermo, nessuna persona lo compila mai.
  const [honeypot, setHoneypot] = useState("");
  // Riepilogo scritto dal bot quando ha aperto lui il modulo: il visitatore
  // lo vede e lo puo' correggere, e parte cosi' come l'ha lasciato.
  const hasSummary = typeof card.summary === "string" && card.summary.length > 0;
  const [summary, setSummary] = useState(hasSummary ? card.summary : "");
  // Stato dei campi file: { status: "uploading" | "done", name, size }.
  const [files, setFiles] = useState({});
  const uploading = Object.values(files).some((f) => f?.status === "uploading");

  const chooseFile = async (field, file) => {
    const accepted = FILE_ACCEPT[field.accept] || FILE_ACCEPT.both;
    if (file.size > FILE_MAX_BYTES) {
      setErrors((prev) => ({ ...prev, [field.id]: "Il file supera i 10 MB" }));
      return;
    }
    // Alcuni browser non dichiarano il tipo: in quel caso decide il server.
    if (file.type && !accepted.includes(file.type)) {
      setErrors((prev) => ({ ...prev, [field.id]: `${FILE_HINT[field.accept] || FILE_HINT.both}: questo tipo di file non è accettato` }));
      return;
    }

    setErrors((prev) => ({ ...prev, [field.id]: undefined }));
    setValues((prev) => ({ ...prev, [field.id]: "" }));
    setFiles((prev) => ({ ...prev, [field.id]: { status: "uploading", name: file.name, size: file.size } }));

    // Un caricamento che si rompe non deve lasciare il campo "in caricamento".
    let result;
    try {
      result = onUpload
        ? await onUpload({ fieldId: field.id, file })
        : { ok: false, errors: ["Caricamento non disponibile."] };
    } catch {
      result = { ok: false, errors: ["Caricamento non riuscito"] };
    }

    if (result?.ok) {
      setValues((prev) => ({ ...prev, [field.id]: result.upload_id }));
      setFiles((prev) => ({
        ...prev,
        [field.id]: { status: "done", name: result.name || file.name, size: result.size || file.size },
      }));
    } else {
      setFiles((prev) => ({ ...prev, [field.id]: undefined }));
      setErrors((prev) => ({ ...prev, [field.id]: (result?.errors || ["Caricamento non riuscito"])[0] }));
    }
  };

  const removeFile = (field) => {
    setValues((prev) => ({ ...prev, [field.id]: "" }));
    setFiles((prev) => ({ ...prev, [field.id]: undefined }));
  };

  const setValue = (id, value) => {
    setValues((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => (prev[id] ? { ...prev, [id]: undefined } : prev));
  };

  const toggleMulti = (field, option) => {
    const current = Array.isArray(values[field.id]) ? values[field.id] : [];
    const next = current.includes(option)
      ? current.filter((v) => v !== option)
      : [...current, option];
    setValue(field.id, next);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (sending) return;

    const found = validateLocally(fields, values, files);
    setErrors(found);
    setServerErrors([]);
    if (Object.values(found).some(Boolean)) return;

    // Un invio che si rompe non deve lasciare il pulsante bloccato su "Invio...".
    setSending(true);
    let result;
    try {
      result = await onSubmit({
        values,
        topic: card.topic || "",
        honeypot,
        ...(hasSummary ? { summary: summary.trim() } : {}),
      });
    } catch {
      result = { ok: false };
    } finally {
      setSending(false);
    }

    if (result?.ok) {
      setSentMessage(result.thank_you || card.thank_you || "Grazie, abbiamo ricevuto la tua richiesta.");
    } else {
      setServerErrors(result?.errors || ["Invio non riuscito. Riprova fra poco."]);
    }
  };

  if (sentMessage) {
    return (
      <div className="puw2-contact-form puw2-contact-form-done" role="status">
        <svg className="puw2-contact-done-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <p className="puw2-contact-done-text">{sentMessage}</p>
      </div>
    );
  }

  return (
    <form className="puw2-contact-form" onSubmit={submit} noValidate>
      <h3 className="puw2-contact-title">{card.title || "Lasciaci i tuoi dati"}</h3>
      {card.description ? <p className="puw2-contact-desc">{card.description}</p> : null}

      {/* Chi ha gia' spiegato cosa gli serve non deve riscriverlo: il bot lo
          ha riassunto, il visitatore controlla e corregge. */}
      {hasSummary ? (
        <div className="puw2-contact-field">
          <label className="puw2-contact-label" htmlFor="puw2-summary">
            La tua richiesta
          </label>
          <textarea
            id="puw2-summary"
            className="puw2-contact-input"
            rows={3}
            maxLength={600}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
          <p className="puw2-contact-hint">L'abbiamo riassunta dalla chat: correggila se serve.</p>
        </div>
      ) : summaryNote ? (
        <p className="puw2-contact-hint">Alla richiesta alleghiamo un riepilogo di questa chat.</p>
      ) : null}

      {fields.map((field) => {
        const error = errors[field.id];
        const describedBy = error ? `${field.id}-error` : undefined;

        return (
          <div className={`puw2-contact-field ${error ? "has-error" : ""}`} key={field.id}>
            {field.type === "consent" ? (
              <label className="puw2-contact-consent">
                <input
                  type="checkbox"
                  checked={Boolean(values[field.id])}
                  onChange={(e) => setValue(field.id, e.target.checked)}
                  aria-describedby={describedBy}
                />
                <span>
                  {field.text}
                  {field.required ? <span className="puw2-contact-required"> *</span> : null}
                  {/* Solo link http(s), anche se server e anteprima filtrano gia'. */}
                  {/^https?:\/\//i.test(field.link_url || "") ? (
                    <>
                      {" "}
                      {/* L'informativa del modulo non e' un suggerimento del bot: fuori dal dataLayer. */}
                      <a href={field.link_url} target="_blank" rel="noopener noreferrer" data-pingup-no-track>
                        {field.link_label || "Scopri di più"}
                      </a>
                    </>
                  ) : null}
                </span>
              </label>
            ) : (
              <>
                <label className="puw2-contact-label" htmlFor={`puw2-${field.id}`}>
                  {field.label}
                  {field.required ? <span className="puw2-contact-required"> *</span> : null}
                </label>

                {field.type === "text" ? (
                  field.multiline ? (
                    <textarea
                      id={`puw2-${field.id}`}
                      className="puw2-contact-input"
                      rows={3}
                      placeholder={field.placeholder || ""}
                      value={values[field.id] || ""}
                      onChange={(e) => setValue(field.id, e.target.value)}
                      aria-describedby={describedBy}
                    />
                  ) : (
                    <input
                      id={`puw2-${field.id}`}
                      className="puw2-contact-input"
                      type={
                        field.mode === "email" ? "email" : field.mode === "phone" ? "tel" : "text"
                      }
                      inputMode={field.mode === "phone" ? "tel" : undefined}
                      autoComplete={
                        field.mode === "email" ? "email" : field.mode === "phone" ? "tel" : "on"
                      }
                      placeholder={field.placeholder || ""}
                      value={values[field.id] || ""}
                      onChange={(e) => setValue(field.id, e.target.value)}
                      aria-describedby={describedBy}
                    />
                  )
                ) : null}

                {/* File: si carica appena scelto, il modulo porta solo il
                    riferimento. Un file per campo. */}
                {field.type === "file" ? (
                  files[field.id]?.status === "done" ? (
                    <div className="puw2-contact-file-chosen">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" strokeLinejoin="round" />
                        <path d="M14 3v5h5" strokeLinejoin="round" />
                      </svg>
                      <span className="puw2-contact-file-name">{files[field.id].name}</span>
                      <span className="puw2-contact-file-size">{formatSize(files[field.id].size)}</span>
                      <button
                        type="button"
                        className="puw2-contact-file-remove"
                        onClick={() => removeFile(field)}
                        aria-label={`Rimuovi ${files[field.id].name}`}
                      >
                        Rimuovi
                      </button>
                    </div>
                  ) : (
                    <label
                      className={`puw2-contact-file-pick ${files[field.id]?.status === "uploading" ? "is-uploading" : ""}`}
                    >
                      <input
                        id={`puw2-${field.id}`}
                        type="file"
                        accept={(FILE_ACCEPT[field.accept] || FILE_ACCEPT.both).join(",")}
                        disabled={files[field.id]?.status === "uploading"}
                        aria-describedby={describedBy}
                        onChange={(e) => {
                          const chosen = e.target.files?.[0];
                          // Svuotato subito: si puo' riscegliere lo stesso file dopo un errore.
                          e.target.value = "";
                          if (chosen) chooseFile(field, chosen);
                        }}
                      />
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M21 11.5l-8.5 8.5a5 5 0 0 1-7-7L14 4.5a3.5 3.5 0 0 1 5 5L10.5 18a2 2 0 0 1-3-3L15 7.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>
                        {files[field.id]?.status === "uploading"
                          ? `Caricamento di ${files[field.id].name}…`
                          : `${FILE_HINT[field.accept] || FILE_HINT.both} · max 10 MB`}
                      </span>
                    </label>
                  )
                ) : null}

                {/* Fino a quattro opzioni si vedono tutte: una tendina che ne
                    nasconde tre non aiuta nessuno. Oltre, la tendina serve. */}
                {field.type === "single_choice" ? (
                  field.options.length <= 4 ? (
                    <div className="puw2-contact-choices" role="radiogroup" aria-label={field.label}>
                      {field.options.map((option) => (
                        <label className="puw2-contact-choice" key={option}>
                          <input
                            type="radio"
                            name={`puw2-${field.id}`}
                            value={option}
                            checked={values[field.id] === option}
                            onChange={() => setValue(field.id, option)}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <select
                      id={`puw2-${field.id}`}
                      className="puw2-contact-input"
                      value={values[field.id] || ""}
                      onChange={(e) => setValue(field.id, e.target.value)}
                      aria-describedby={describedBy}
                    >
                      <option value="">Scegli…</option>
                      {field.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )
                ) : null}

                {field.type === "multi_choice" ? (
                  <>
                    <div className="puw2-contact-choices">
                      {field.options.map((option) => {
                        const chosen = Array.isArray(values[field.id]) ? values[field.id] : [];
                        const isChosen = chosen.includes(option);
                        // Raggiunto il massimo, le altre si spengono invece di
                        // far scoprire il limite solo dopo aver premuto Invia.
                        const blocked = !isChosen && field.max > 0 && chosen.length >= field.max;
                        return (
                          <label
                            className={`puw2-contact-choice ${blocked ? "is-blocked" : ""}`}
                            key={option}
                          >
                            <input
                              type="checkbox"
                              checked={isChosen}
                              disabled={blocked}
                              onChange={() => toggleMulti(field, option)}
                            />
                            <span>{option}</span>
                          </label>
                        );
                      })}
                    </div>
                    {/* Quante scelte, detto prima dell'invio: anche con il solo minimo. */}
                    {(field.max > 0 && field.max < field.options.length) || field.min > 1 ? (
                      <p className="puw2-contact-hint">
                        {field.max > 0 && field.max < field.options.length
                          ? field.min > 0
                            ? `Da ${field.min} a ${field.max} scelte`
                            : `Fino a ${field.max} scelte`
                          : `Almeno ${field.min} scelte`}
                      </p>
                    ) : null}
                  </>
                ) : null}
              </>
            )}

            {error ? (
              <p className="puw2-contact-error" id={`${field.id}-error`} role="alert">
                {error}
              </p>
            ) : null}
          </div>
        );
      })}

      {/* Esca antirobot: fuori schermo e fuori dal giro di tabulazione. Il
          nome non significa nulla di proposito: un campo chiamato "website"
          rischia di essere riempito dall'autocompilazione del browser o da un
          gestore di password, e il contatto di una persona vera verrebbe
          scartato in silenzio. */}
      <div className="puw2-contact-trap" aria-hidden="true">
        <label htmlFor="puw2-hp-field">Lascia vuoto</label>
        <input
          id="puw2-hp-field"
          name="puw2_hp_field"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      {serverErrors.length ? (
        <div className="puw2-contact-server-errors" role="alert">
          {serverErrors.map((message, idx) => (
            <p key={idx}>{message}</p>
          ))}
        </div>
      ) : null}

      <button type="submit" className="puw2-contact-submit" disabled={sending || uploading}>
        {sending ? "Invio…" : uploading ? "Caricamento…" : card.submit_label || "Invia"}
      </button>
    </form>
  );
}
